import { Button, DropdownMenu, FormInput } from "@/components/bnp-ui";
import React, { useEffect, useState } from "react";
import Summary from "./Summary";
import ImageUpload from "./ImageUpload";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { PropertyService } from "@/services";
import { useCompatRouter as useRouter } from "@/lib/compat-router/context";
import { Switch } from "@mantine/core";
import { getLatLng } from "@/utils/location";
import PlacesAutocomplete from "./AddressDropDown";

import {
  getPropertyTypesByCategory,
  getPropertyTypeLabel,
  getPropertyTypeValueByLabel,
  PROPERTY_CATEGORIES,
} from "./config/propertyCategories";
import CategorySpecificFields from "./CategorySpecificFields";
import parseHttpError from "@/utils/parseHttpError";
import { usePostHogAnalytics } from "@/contexts/PostHogAnalyticsContext";
import { useAuth } from "@/contexts/AuthContext";

export const PROPERTY_TYPE_OPTIONS_FOR_MAPPING = Object.fromEntries(
  Object.entries({
    house: "House",
    "flat-and-apartments": "Flat / Apartment",
    "mini-flats": "Mini Flat",
    "self-contain": "Self Contain",
    terraces: "Terrace",
    "detached-bungalows": "Detached Bungalow",
    "semi-detached-duplex": "Semi-detached Duplex",
    "fully-detached-duplex": "Fully-detached Duplex",
    penthouse: "Penthouse",
    maisonette: "Maisonette",
    "block-of-flats": "Block of Flats",
    "office-spaces": "Office Space",
    "co-working-space": "Co-working Space",
    shops: "Shop",
    "shop-in-a-mall": "Shop in Mall",
    "commercial-properties": "Commercial Building",
    plaza: "Plaza",
    "retail-space": "Retail Space",
    showroom: "Showroom",
    "restaurant-cafe": "Restaurant / Café",
    "hotel-guest-house": "Hotel / Guest House",
    "event-centre": "Event Centre",
    warehouses: "Warehouse",
    factory: "Factory",
    workshop: "Workshop",
    "cold-room": "Cold Room",
    "logistics-hub": "Logistics Hub",
    school: "School",
    hospital: "Hospital",
    church: "Church",
    mosque: "Mosque",
    "filling-station": "Filling Station",
    "petrol-gas-plant": "Petrol / Gas Plant",
    farm: "Farm",
    "government-property": "Government Property",
  }).map(([value, label]) => [label, value]),
);

export const propertyTypeMapping = PROPERTY_TYPE_OPTIONS_FOR_MAPPING;

export function getPropertyType(value: string): string {
  return (
    Object.entries(PROPERTY_TYPE_OPTIONS_FOR_MAPPING).find(
      ([_, v]) => v === value,
    )?.[0] || value
  );
}

export const propertyStageMapping: Record<string, string> = {
  "Off-Plan": "off-plan",
  Carcass: "carcass",
  "Semi-Finished Carcass": "semi-finish-carcass",
  Finished: "finished",
  "Developed Land": "developed-land",
  "Semi-Developed Land": "semi-developed-land",
  "Un-developed Land": "un-developed-land",
};

const LAND_STAGES = [
  "Developed Land",
  "Semi-Developed Land",
  "Un-developed Land",
];
const SALE_STAGES = [
  "Off-Plan",
  "Carcass",
  "Semi-Finished Carcass",
  "Finished",
];

const DOCUMENT_OPTIONS = [
  "CoFO",
  "Governor's Consent",
  "Deed of Assignment",
  "Excision",
  "Right of Ownership (RoFO)",
  "Approved Survey",
  "CoFO in processing",
  "Governor's Consent in processing",
  "Deed of Assignment in processing",
  "Excision in processing",
  "RoFO in processing",
  "Survey in processing",
  "Gazette",
];

interface CreatePropertyFormProps {
  listingType: "rent" | "sale" | "shortlet" | "land";
}

const CreatePropertyForm = ({ listingType }: CreatePropertyFormProps) => {
  const router = useRouter();
  const [step, setStep] = useState(1);
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [isFurnished, setIsFurnished] = useState(false);
  const [isServiced, setIsServiced] = useState(false);
  const [isShared, setIsShared] = useState(false);
  const [isNegotiable, setIsNegotiable] = useState(false);
  const [promoteWeekly, setPromoteWeekly] = useState(false);
  const [requestInspection, setRequestInspection] = useState(false);
  const [uploadedUrls, setUploadedUrls] = useState<string[]>([]);
  const [legalFeeHint, setLegalFeeHint] = useState("");
  const [latitude, setLatitude] = useState<number | undefined>(undefined);
  const [longitude, setLongitude] = useState<number | undefined>(undefined);
  const [category, setCategory] = useState<string>("residential");

  const [files, setFiles] = useState<File[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<string[]>([]);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>(
    {},
  );
  const [imageLoadingStates, setImageLoadingStates] = useState<
    Record<string, boolean>
  >({});

  const { isDeveloper, isRealtor } = useAuth();
  const { trackListingSubmitted } = usePostHogAnalytics();

  const isLand = listingType === "land";
  const isShortlet = listingType === "shortlet";
  const isSale = listingType === "sale";
  const isRent = listingType === "rent";

  const defaultValues: Record<string, any> = {
    address: "",
    status: "available",
    listingType,
    price: undefined,
    propertyType: "",
    category: isLand ? "" : category,
    currency: "",
    furnished: isFurnished,
    serviced: isServiced,
    shared: isShared,
    is_negotiable: isNegotiable,
    bedroomCount: undefined,
    bathroomCount: undefined,
    agencyFees: undefined,
    legalFees: undefined,
    cautionFee: undefined,
    totalPackage: undefined,
    additionalInfo: additionalInfo,
    description: "",
    imageUrl: uploadedUrls,
    promoteWeekly: promoteWeekly,
    requestInspection: requestInspection,
    amenities: [],
    latitude: undefined,
    longitude: undefined,
    // Sale-specific
    propertyStage: "",
    saleDocumentDetails: "",
    // Land-specific
    numberOfPlots: undefined,
    documentation: "",
    // Shortlet-specific
    numberOfDays: undefined,
  };

  const buildValidationSchema = () => {
    const sharedShape: Record<string, any> = {
      address: yup
        .string()
        .required("Address is required")
        .max(100, "Address must be 100 characters or less"),
      status: yup.string().required("Status is required"),
      listingType: yup.string().required("Listing type is required"),
      price: yup
        .number()
        .min(0, "Price must be 0 or more")
        .required("Price is required"),
      currency: yup.string().required("Currency is required"),
      is_negotiable: yup.boolean().required("Negotiable status is required"),
      promoteWeekly: yup.boolean().required("Promotion status is required"),
      requestInspection: yup
        .boolean()
        .required("Inspection status is required"),
      description: yup
        .string()
        .required("Title is required")
        .matches(
          /^[A-Za-z0-9\s]+$/,
          "Title can only contain letters, numbers and spaces",
        )
        .max(200, "Title must be 200 characters or less"),
      additionalInfo: yup.string(),
      imageUrl: yup.array(),
      amenities: yup.array(),
      agencyFees: yup
        .number()
        .min(0, "Agency fee must be 0 or more")
        .required("Agency fee is required")
        .test(
          "agency-lte-price",
          "Agency fee cannot exceed the property price",
          function (value) {
            const price = Number(this.parent.price) || 0;
            if (!value || price <= 0) return true;
            return value <= price;
          },
        ),
      legalFees: yup
        .number()
        .typeError("Enter a valid amount")
        .min(0, "Legal fee must be 0 or more")
        .required("Legal fee is required")
        .test(
          "legal-lte-price",
          "Legal fee cannot exceed the property price",
          function (value) {
            const price = Number(this.parent.price) || 0;
            if (!value || price <= 0) return true;
            return value <= price;
          },
        ),
      totalPackage: yup.number().required("Total package is required"),
    };

    // Category & property type only for non-land
    if (!isLand) {
      sharedShape.category = yup.string().required("Category is required");
      sharedShape.propertyType = yup
        .string()
        .required("Property type is required");
    }

    // Residential-only fields (bedroom/bathroom count)
    if (!isLand) {
      sharedShape.bedroomCount = yup
        .number()
        .transform((value, originalValue) =>
          originalValue === "" ? undefined : value,
        )
        .nullable()
        .when("category", {
          is: "residential",
          then: (schema: any) =>
            schema
              .required("Bedroom count is required")
              .min(1, "Bedroom count must be 1 or more")
              .max(100, "Bedroom count must be 100 or less"),
          otherwise: (schema: any) => schema.notRequired(),
        });

      sharedShape.bathroomCount = yup
        .number()
        .transform((value, originalValue) =>
          originalValue === "" ? undefined : value,
        )
        .nullable()
        .when("category", {
          is: "residential",
          then: (schema: any) =>
            schema
              .required("Bathroom count is required")
              .min(1, "Bathroom count must be 1 or more")
              .max(100, "Bathroom count must be 100 or less"),
          otherwise: (schema: any) => schema.notRequired(),
        });
    }

    // Sale-specific fields
    if (isSale) {
      sharedShape.propertyStage = yup
        .string()
        .required("Property stage is required");
      sharedShape.saleDocumentDetails = yup.string();
    }

    // Land-specific fields
    if (isLand) {
      sharedShape.numberOfPlots = yup
        .number()
        .required("Number of square meters is required")
        .min(1, "Number of square meters must be 1 or more");
      sharedShape.PropertyStage = yup
        .string()
        .required("Property stage is required");
      sharedShape.documentation = yup.string();
    }

    // Shortlet-specific fields
    if (isShortlet) {
      sharedShape.numberOfDays = yup
        .number()
        .required("Minimum days is required")
        .min(1, "Minimum days must be 1 or more");
      sharedShape.cautionFee = yup
        .number()
        .min(0, "Caution fee must be 0 or more")
        .required("Caution fee is required");
    } else {
      sharedShape.cautionFee = yup
        .number()
        .min(0, "Caution fee must be 0 or more")
        .required("Caution fee is required");
    }

    // Furnished/serviced/shared toggles only for non-land
    if (!isLand) {
      sharedShape.furnished = yup
        .boolean()
        .required("Furnished status is required");
      sharedShape.serviced = yup
        .boolean()
        .required("Serviced status is required");
      if (isRent) {
        sharedShape.shared = yup
          .boolean()
          .required("Shared status is required");
      }
    }

    return yup.object().shape(sharedShape);
  };

  const [validationSchema, setValidationSchema] = useState(() =>
    buildValidationSchema(),
  );

  useEffect(() => {
    setValidationSchema(buildValidationSchema());
  }, [listingType, category]);

  const {
    getValues,
    setValue,
    control,
    handleSubmit,
    watch,
    register,
    trigger,
    formState: { errors },
  } = useForm({
    defaultValues,
    resolver: yupResolver(validationSchema),
    mode: "onChange",
  });

  const price = watch("price");
  const agencyFees = watch("agencyFees");
  const legalFees = watch("legalFees");
  const cautionFee = watch("cautionFee");
  const watchedAmenities = watch("amenities") || [];
  const watchedPropertyType = watch("propertyType") || "";

  // Auto-calculate total package
  useEffect(() => {
    const basePrice = Number(price) || 0;
    const baseAgencyFee = Number(agencyFees) || 0;
    const baseLegalFee = Number(legalFees) || 0;
    const baseCautionFee = Number(cautionFee) || 0;
    const totalPackage =
      basePrice + baseAgencyFee + baseCautionFee + baseLegalFee;
    setValue("totalPackage", totalPackage, { shouldValidate: true });
  }, [price, agencyFees, legalFees, cautionFee, setValue]);

  const handleNext = async () => {
    const isStepValid = await trigger();
    if (isStepValid) {
      setStep(step + 1);
    } else {
      toast.error("Please fill in all required fields correctly");
    }
  };

  const addPropertyRequest = async () => {
    try {
      setValue("additionalInfo", additionalInfo);
      setValue("imageUrl", uploadedUrls);
      const address = getValues().address;
      let lat = latitude;
      let lng = longitude;
      if (address && (lat === undefined || lng === undefined)) {
        const coords = await getLatLng(address);
        lat = coords.lat;
        lng = coords.lng;
      }
      setValue("latitude", lat);
      setValue("longitude", lng);
      const formData = getValues();
      const payload: Record<string, any> = {
        ...formData,
        image_url: uploadedUrls,
        amenities: watchedAmenities,
        latitude: lat,
        longitude: lng,
      };
      if (!isLand) {
        payload.category = category;
      }
      const response = await PropertyService.AddProperty(payload as any);
      const created = response?.data?.data ?? response?.data;
      trackListingSubmitted({
        listing_id:
          created?.id ?? created?.propertyId ?? created?.property_id ?? "",
        property_type: payload.propertyType,
        listing_type: payload.listingType,
        location_city: payload.city,
        location_area: payload.address,
        price: Number(payload.price) || undefined,
        user_type: isDeveloper
          ? "developer"
          : isRealtor
            ? "realtor"
            : "buyer_renter",
      });
      return response?.data;
    } catch (error: any) {
      console.log(error);
      throw new Error(parseHttpError(error).message);
    }
  };

  const mutation: any = useMutation({
    mutationFn: addPropertyRequest,
    onSuccess: () => {
      toast.success("Property added!");
      router.reload();
    },
    onError: (error: any) => {
      const msg = (error.message || "").toLowerCase();
      if (
        msg.includes("realtor not found") ||
        msg.includes("business not found") ||
        msg.includes("do not have permission")
      ) {
        toast.error(
          "Please complete your onboarding before listing a property.",
        );
        setTimeout(() => router.push("/auth/onboarding"), 2000);
      } else {
        toast.error(error.message || "Request failed");
      }
    },
  });

  const onSubmit = handleSubmit(
    () => {
      mutation.mutate();
    },
    (errs) => {
      toast.error("Please check all required fields");
      console.log(errs);
    },
  );

  const handleLatLngChange = (lat: number, lng: number) => {
    setLatitude(lat);
    setLongitude(lng);
  };

  const stageOptions = isLand ? LAND_STAGES : isSale ? SALE_STAGES : [];

  return (
    <div>
      {step === 1 && (
        <form
          action={"submit"}
          onSubmit={(e) => e.preventDefault()}
          className="border-b border-soft-200 mb-4 pb-4"
        >
          {/* Title */}
          <div className="w-[100%] flex flex-col lg:items-center lg:flex-row gap-3 mb-4">
            <Controller
              name="description"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <div className="w-full">
                  <FormInput
                    type="text"
                    name="description"
                    value={value}
                    label="Title"
                    placeholder="e.g. Modern Two Bedroom Apartment in Abuja"
                    className="w-full h-[48px]"
                    onChange={onChange}
                    required
                  />
                  {errors?.description && (
                    <p className="text-red-500 text-sm">
                      {errors?.description?.message as string}
                    </p>
                  )}
                </div>
              )}
            />
          </div>

          {/* Land-specific: Number of plots + Property stage */}
          {isLand && (
            <div className="flex flex-col lg:items-center lg:flex-row gap-3 mb-4">
              <Controller
                name="numberOfPlots"
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <div>
                    <FormInput
                      type="number"
                      value={value}
                      name="numberOfPlots"
                      label="Number of square meters"
                      placeholder="Number of square meters"
                      className="w-[100%] lg:w-[380px] h-[48px]"
                      required
                      onChange={onChange}
                    />
                    {errors?.numberOfPlots && (
                      <p className="text-red-500 text-sm">
                        {errors?.numberOfPlots?.message as string}
                      </p>
                    )}
                  </div>
                )}
              />
              <Controller
                name="PropertyStage"
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <div className="w-[100%] lg:w-[380px] flex flex-col gap-1">
                    <p className="text-sm font-medium text-main-900">
                      Property stage
                    </p>
                    <DropdownMenu
                      name="PropertyStage"
                      value={value}
                      options={LAND_STAGES}
                      onChange={(e) => {
                        const apiValue = propertyStageMapping[e.target.value];
                        onChange(apiValue);
                      }}
                    />
                    {errors?.PropertyStage && (
                      <p className="text-red-500 text-sm">
                        {errors?.PropertyStage?.message as string}
                      </p>
                    )}
                  </div>
                )}
              />
            </div>
          )}

          {/* Sale-specific: Property stage */}
          {isSale && (
            <div className="flex flex-col lg:items-center lg:flex-row gap-3 mb-4">
              <Controller
                name="propertyStage"
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <div className="w-[100%] lg:w-[380px] flex flex-col gap-1">
                    <p className="text-sm font-medium text-main-900">
                      Property stage{" "}
                      <span className="text-red-500 text-sm">*</span>
                    </p>
                    <DropdownMenu
                      name="propertyStage"
                      value={value}
                      options={SALE_STAGES}
                      onChange={(e) => {
                        const apiValue = propertyStageMapping[e.target.value];
                        onChange(apiValue);
                      }}
                    />
                    {errors?.propertyStage && (
                      <p className="text-red-500 text-sm">
                        {errors?.propertyStage?.message as string}
                      </p>
                    )}
                  </div>
                )}
              />
            </div>
          )}

          {/* Shortlet-specific: Minimum days */}
          {isShortlet && (
            <div className="flex flex-col lg:items-center lg:flex-row gap-3 mb-4">
              <Controller
                name="numberOfDays"
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <div>
                    <FormInput
                      type="number"
                      value={value}
                      name="numberOfDays"
                      label="Minimum days"
                      placeholder="Number of days"
                      className="w-[100%] lg:w-[380px] h-[48px]"
                      required
                      onChange={onChange}
                    />
                    {errors?.numberOfDays && (
                      <p className="text-red-500 text-sm">
                        {errors?.numberOfDays?.message as string}
                      </p>
                    )}
                  </div>
                )}
              />
            </div>
          )}

          {/* Address & Location */}
          <div className="flex flex-col lg:items-center lg:flex-row gap-3">
            <PlacesAutocomplete
              control={control}
              errors={errors}
              setLatLng={handleLatLngChange}
            />
          </div>

          {/* Category & Property Type (hidden for Land) */}
          {!isLand && (
            <div className="mt-4 flex flex-col lg:items-center lg:flex-row gap-3">
              <Controller
                name="category"
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <div className="w-[100%] lg:w-[50%] flex flex-col gap-1">
                    <p className="text-sm font-medium text-main-900">
                      Category <span className="text-red-500 text-sm">*</span>
                    </p>
                    <select
                      name="category"
                      value={value}
                      onChange={(e) => {
                        onChange(e.target.value);
                        setCategory(e.target.value);
                        setValue("propertyType", "");
                      }}
                      className="w-full px-4 py-3 text-gray-600 bg-white shadow-sm border border-soft-200 rounded-lg focus:outline-none focus:border-bnp-cyan transition-colors"
                    >
                      {Object.entries(PROPERTY_CATEGORIES).map(([key, cat]) => (
                        <option key={key} value={key}>
                          {cat.label}
                        </option>
                      ))}
                    </select>
                    {errors?.category && (
                      <p className="text-red-500 text-sm">
                        {errors?.category?.message as string}
                      </p>
                    )}
                  </div>
                )}
              />

              <Controller
                name="propertyType"
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <div className="w-[100%] lg:w-[50%] flex flex-col gap-1">
                    <p className="text-sm font-medium text-main-900">
                      Property type{" "}
                      <span className="text-red-500 text-sm">*</span>
                    </p>
                    <DropdownMenu
                      name="propertyType"
                      value={getPropertyTypeLabel(watchedPropertyType)}
                      options={getPropertyTypesByCategory(category).map(
                        (type) => getPropertyTypeLabel(type),
                      )}
                      onChange={(e) => {
                        onChange(getPropertyTypeValueByLabel(e.target.value));
                      }}
                    />
                    {errors?.propertyType && (
                      <p className="text-red-500 text-sm">
                        {errors?.propertyType?.message as string}
                      </p>
                    )}
                  </div>
                )}
              />
            </div>
          )}

          {/* Sale-specific: Document type */}
          {isSale && !isLand && (
            <div className="mt-4 flex flex-col lg:items-center lg:flex-row gap-3">
              <Controller
                name="saleDocumentDetails"
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <div className="w-[100%] lg:w-1/2 flex flex-col gap-1">
                    <p className="text-sm font-medium text-main-900">
                      Document type (optional)
                    </p>
                    <DropdownMenu
                      name="saleDocumentDetails"
                      value={value}
                      options={DOCUMENT_OPTIONS}
                      onChange={onChange}
                    />
                    {errors?.saleDocumentDetails && (
                      <p className="text-red-500 text-sm">
                        {errors?.saleDocumentDetails?.message as string}
                      </p>
                    )}
                  </div>
                )}
              />
            </div>
          )}

          {/* Land-specific: Documentation type */}
          {isLand && (
            <div className="mt-4 flex flex-col lg:items-center lg:flex-row gap-3">
              <Controller
                name="documentation"
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <div className="w-[100%] lg:w-1/2 flex flex-col gap-1">
                    <p className="text-sm font-medium text-main-900">
                      Documentation type (optional)
                    </p>
                    <DropdownMenu
                      name="documentation"
                      value={value}
                      options={DOCUMENT_OPTIONS}
                      onChange={onChange}
                    />
                    {errors?.documentation && (
                      <p className="text-red-500 text-sm">
                        {errors?.documentation?.message as string}
                      </p>
                    )}
                  </div>
                )}
              />
            </div>
          )}

          {/* Residential-specific fields (bedroom, bathroom, furnished, serviced, shared) */}
          {!isLand && (
            <>
              <div className="w-[100%] items-center flex flex-col lg:flex-row gap-3 mt-4">
                <Controller
                  name="bedroomCount"
                  control={control}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <div className="mt-2 w-[100%] lg:w-1/3">
                      <FormInput
                        type="number"
                        value={value}
                        name="bedroomCount"
                        label="Bedroom count"
                        placeholder="Bedroom count"
                        className="w-full h-[48px]"
                        required
                        onChange={onChange}
                      />
                      {errors?.bedroomCount && (
                        <p className="text-red-500 text-sm">
                          {errors?.bedroomCount?.message as string}
                        </p>
                      )}
                    </div>
                  )}
                />

                {/* <Controller
                  name="bathroomCount"
                  control={control}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <div className="mt-2 w-[100%] lg:w-1/3">
                      <FormInput
                        type="number"
                        value={value}
                        name="bathroomCount"
                        label="Bathroom count"
                        placeholder="Bathroom count"
                        className="w-full h-[48px]"
                        required
                        onChange={onChange}
                      />
                      {errors?.bathroomCount && (
                        <p className="text-red-500 text-sm">{errors?.bathroomCount?.message as string}</p>
                      )}
                    </div>
                  )}
                />

                <Controller
                  name="price"
                  control={control}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <div className="mt-2 w-[100%] lg:w-1/3">
                      <FormInput
                        type="number"
                        value={value}
                        name="price"
                        label="Price"
                        placeholder="Enter price"
                        className="w-full h-[48px]"
                        required
                        onChange={onChange}
                      />
                      {errors?.price && (
                        <p className="text-red-500 text-sm">{errors?.price?.message as string}</p>
                      )}
                    </div>
                  )}
                />
              </div>

              <div className="w-[100%] mt-4 flex flex-col lg:items-center lg:flex-row gap-3">
                <div className="w-[100%] lg:w-1/4">
                  <p className="text-sm text-main-900 font-medium">
                    Furnished? <span className="text-red-500 text-sm">*</span>
                  </p>
                  <div className="flex gap-2 items-center mt-4">
                    <div className="flex items-center gap-2 w-[81.5px]">
                      <label>Yes</label>
                      <input
                        type="radio"
                        checked={isFurnished}
                        onChange={() => {
                          setIsFurnished(true);
                          setValue("furnished", true);
                        }}
                        name="furnished"
                      />
                    </div>
                    <div className="flex items-center gap-2 w-[81.5px]">
                      <label>No</label>
                      <input
                        type="radio"
                        checked={!isFurnished}
                        onChange={() => {
                          setIsFurnished(false);
                          setValue("furnished", false);
                        }}
                        name="furnished"
                      />
                    </div>
                  </div>
                  {errors?.furnished && (
                    <p className="text-red-500 text-sm">{errors?.furnished?.message as string}</p>
                  )}
                </div>

                <div className="w-[100%] lg:w-1/4">
                  <p className="text-sm text-main-900 font-medium">
                    Serviced? <span className="text-red-500 text-sm">*</span>
                  </p>
                  <div className="flex gap-2 items-center mt-4">
                    <div className="flex items-center gap-2 w-[81.5px]">
                      <label>Yes</label>
                      <input
                        type="radio"
                        checked={isServiced}
                        onChange={() => {
                          setIsServiced(true);
                          setValue("serviced", true);
                        }}
                        name="serviced"
                      />
                    </div>
                    <div className="flex items-center gap-2 w-[81.5px]">
                      <label>No</label>
                      <input
                        type="radio"
                        checked={!isServiced}
                        onChange={() => {
                          setIsServiced(false);
                          setValue("serviced", false);
                        }}
                        name="serviced"
                      />
                    </div>
                  </div>
                  {errors?.serviced && (
                    <p className="text-red-500 text-sm">{errors?.serviced?.message as string}</p>
                  )}
                </div> */}

                {isRent && (
                  <div className="w-[100%] lg:w-1/4">
                    <p className="text-sm text-main-900 font-medium">
                      Shared? <span className="text-red-500 text-sm">*</span>
                    </p>
                    <div className="flex gap-2 items-center mt-4">
                      <div className="flex items-center gap-2 w-[81.5px]">
                        <label>Yes</label>
                        <input
                          type="radio"
                          checked={isShared}
                          onChange={() => {
                            setIsShared(true);
                            setValue("shared", true);
                          }}
                          name="shared"
                        />
                      </div>
                      <div className="flex items-center gap-2 w-[81.5px]">
                        <label>No</label>
                        <input
                          type="radio"
                          checked={!isShared}
                          onChange={() => {
                            setIsShared(false);
                            setValue("shared", false);
                          }}
                          name="shared"
                        />
                      </div>
                    </div>
                    {errors?.shared && (
                      <p className="text-red-500 text-sm">
                        {errors?.shared?.message as string}
                      </p>
                    )}
                  </div>
                )}

                <div className="w-[100%] lg:w-1/4">
                  <p className="text-sm text-main-900 font-medium">
                    Price Negotiable?{" "}
                    <span className="text-red-500 text-sm">*</span>
                  </p>
                  <div className="flex gap-2 items-center mt-4">
                    <div className="flex items-center gap-2 w-[81.5px]">
                      <label>Yes</label>
                      <input
                        type="radio"
                        checked={isNegotiable}
                        onChange={() => {
                          setIsNegotiable(true);
                          setValue("is_negotiable", true);
                        }}
                        name="is_negotiable"
                      />
                    </div>
                    <div className="flex items-center gap-2 w-[81.5px]">
                      <label>No</label>
                      <input
                        type="radio"
                        checked={!isNegotiable}
                        onChange={() => {
                          setIsNegotiable(false);
                          setValue("is_negotiable", false);
                        }}
                        name="is_negotiable"
                      />
                    </div>
                  </div>
                  {errors?.is_negotiable && (
                    <p className="text-red-500 text-sm">
                      {errors?.is_negotiable?.message as string}
                    </p>
                  )}
                </div>
              </div>

              <Controller
                name="currency"
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <div className="w-[100%] lg:w-1/4 flex flex-col gap-1 mt-4">
                    <p className="text-sm font-medium text-main-900">
                      Currency <span className="text-red-500 text-sm">*</span>
                    </p>
                    <DropdownMenu
                      name="currency"
                      value={value}
                      options={["NGN", "USD", "GBP"]}
                      onChange={onChange}
                    />
                    {errors?.currency && (
                      <p className="text-red-500 text-sm">
                        {errors?.currency?.message as string}
                      </p>
                    )}
                  </div>
                )}
              />
            </>
          )}

          {/* Category-specific fields (dynamic based on category + property type) */}
          {!isLand && (
            <CategorySpecificFields
              category={category}
              propertyType={watchedPropertyType}
              register={register}
              errors={errors}
              control={control}
              watch={watch}
            />
          )}

          {/* Pricing section */}
          <div className="flex flex-col lg:items-start lg:flex-row gap-3 mb-4 mt-4">
            <Controller
              name="price"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <div className="mt-5 w-[100%] lg:w-1/4">
                  <FormInput
                    type="number"
                    value={value}
                    name="price"
                    label="Price"
                    placeholder="Enter price"
                    className="w-full h-[48px]"
                    onChange={onChange}
                    required
                  />
                  {errors?.price && (
                    <p className="text-red-500 text-sm">
                      {errors?.price?.message as string}
                    </p>
                  )}
                </div>
              )}
            />

            <Controller
              name="agencyFees"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <div className="mt-5 w-[100%] lg:w-1/4">
                  <FormInput
                    type="number"
                    value={value}
                    name="agencyFees"
                    label="Agency fee"
                    placeholder="Enter fee"
                    className="w-full h-[48px]"
                    onChange={onChange}
                    disabled={promoteWeekly}
                    required
                  />
                  {errors?.agencyFees && (
                    <p className="text-red-500 text-sm">
                      {errors?.agencyFees?.message as string}
                    </p>
                  )}
                </div>
              )}
            />

            <Controller
              name="legalFees"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <div className="mt-5 w-[100%] lg:w-1/4">
                  <FormInput
                    type="text"
                    value={value}
                    name="legalFees"
                    label="Legal fee"
                    placeholder="Enter fee or percentage e.g. 30%"
                    className="w-full h-[48px]"
                    onChange={(e: any) => {
                      const raw = String(e?.target?.value ?? e ?? "");
                      const percentMatch = raw
                        .replace(/\s/g, "")
                        .match(/^(\d+(?:\.\d+)?)%$/);
                      if (percentMatch) {
                        const percent = parseFloat(percentMatch[1]);
                        const propertyPrice = Number(price) || 0;
                        const computed = Math.round(
                          (propertyPrice * percent) / 100,
                        );
                        onChange(computed);
                        setLegalFeeHint(
                          propertyPrice > 0
                            ? `${percent}% of ₦${propertyPrice.toLocaleString()} = ₦${computed.toLocaleString()}`
                            : "Enter property price first to convert percentage",
                        );
                      } else {
                        onChange(e);
                        setLegalFeeHint("");
                      }
                    }}
                    required
                  />
                  {legalFeeHint && (
                    <p className="text-green-600 text-sm mt-1">
                      {legalFeeHint}
                    </p>
                  )}
                  {errors?.legalFees && (
                    <p className="text-red-500 text-sm">
                      {errors?.legalFees?.message as string}
                    </p>
                  )}
                </div>
              )}
            />

            <Controller
              name="cautionFee"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <div className="mt-5 w-[100%] lg:w-1/4">
                  <FormInput
                    type="number"
                    value={value}
                    name="cautionFee"
                    label="Caution fee"
                    placeholder="Enter fee"
                    className="w-full h-[48px]"
                    onChange={onChange}
                    required
                  />
                  {errors?.cautionFee && (
                    <p className="text-red-500 text-sm">
                      {errors?.cautionFee?.message as string}
                    </p>
                  )}
                </div>
              )}
            />

            <Controller
              name="totalPackage"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <div className="mt-5 w-[100%] lg:w-1/4">
                  <FormInput
                    type="number"
                    value={value}
                    name="totalPackage"
                    label="Total package"
                    placeholder="Auto-calculated"
                    className="w-full h-[48px]"
                    required
                    disabled
                    onChange={onChange}
                  />
                  {errors?.totalPackage && (
                    <p className="text-red-500 text-sm">
                      {errors?.totalPackage?.message as string}
                    </p>
                  )}
                </div>
              )}
            />
          </div>

          {/* Switches */}
          <Switch
            checked={promoteWeekly}
            className="cursor-pointer"
            label={
              <div className="flex flex-col gap-1 mt-[-4px]">
                <p className="text-base font-medium text-main-900">
                  Promote to weekly deals
                </p>
                <p className="text-sm text-grey-7">
                  Listings pushed to weekly deals connote that customers will
                  not be paying agency fee on the transactions.
                </p>
              </div>
            }
            onChange={() => {
              setPromoteWeekly(!promoteWeekly);
              if (!promoteWeekly) {
                setValue("agencyFees", 0);
              }
            }}
          />
          <Switch
            checked={requestInspection}
            className="cursor-pointer mt-5"
            label={
              <div className="flex flex-col gap-1 mt-[-4px]">
                <p className="text-base font-medium text-main-900">
                  Request Live Inspection
                </p>
                <p className="text-sm text-grey-7">
                  This will enable this property to be available for live
                  inspection requests to be carried out on behalf of the
                  potential tenants.
                </p>
              </div>
            }
            onChange={() => {
              setRequestInspection(!requestInspection);
            }}
          />
        </form>
      )}

      <div className={step === 2 ? "block" : "hidden"}>
        <ImageUpload
          additionalInfo={additionalInfo}
          setAdditionalInfo={setAdditionalInfo}
          uploadedUrls={uploadedUrls}
          setUploadedUrls={setUploadedUrls}
          files={files}
          setFiles={setFiles}
          uploadingFiles={uploadingFiles}
          setUploadingFiles={setUploadingFiles}
          uploadProgress={uploadProgress}
          setUploadProgress={setUploadProgress}
          imageLoadingStates={imageLoadingStates}
          setImageLoadingStates={setImageLoadingStates}
          additionalInfoView
        />
      </div>

      {step === 3 && (
        <Summary
          uploadedUrls={uploadedUrls}
          price={getValues().price}
          bedroomCount={getValues().bedroomCount}
          bathroomCount={getValues().bathroomCount}
          propertyType={getValues().propertyType}
          furnished={isFurnished ? "Yes" : "No"}
          city={getValues().city}
          address={getValues().address}
          legalFees={getValues().legalFees}
          agencyFees={getValues().agencyFees}
          cautionFee={getValues().cautionFee}
          totalPackage={getValues().totalPackage}
          shared={isShared ? "Yes" : "No"}
          serviced={isServiced ? "Yes" : "No"}
          negotiable={isNegotiable ? "Yes" : "No"}
          currency={getValues().currency}
          category={isLand ? "land" : category}
          amenities={watchedAmenities}
          listingType={listingType}
          numberOfPlots={getValues().numberOfPlots}
        />
      )}

      <div className="flex w-[100%] justify-between">
        <div className="flex items-center gap-[10px]">
          <div
            className={`w-2 h-2 rounded-full transition-all ${step === 1 ? "bg-bnp-cyan" : "bg-soft-200"}`}
          ></div>
          <div
            className={`w-2 h-2 rounded-full transition-all ${step === 2 ? "bg-bnp-cyan" : "bg-soft-200"}`}
          ></div>
          <div
            className={`w-2 h-2 rounded-full transition-all ${step === 3 ? "bg-bnp-cyan" : "bg-soft-200"}`}
          ></div>
        </div>
        <div className="flex gap-3 items-center mb-8">
          {step > 1 && (
            <Button
              className="bg-white border border-border-1 text-bnp-dark text-sm font-medium w-[94px] h-[60px] rounded-lg"
              onClick={() => setStep(step - 1)}
            >
              Back
            </Button>
          )}
          {step !== 3 && (
            <Button
              className="bg-bnp-dark text-white text-sm font-medium w-[94px] h-[60px] rounded-lg"
              onClick={handleNext}
              disabled={false}
            >
              Next
            </Button>
          )}
          {step === 3 && (
            <Button
              className="bg-bnp-dark text-white text-sm font-medium w-[94px] h-[60px] rounded-lg"
              onClick={onSubmit}
              isLoading={mutation.isPending}
            >
              Submit
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreatePropertyForm;
